import type { ReactNode } from "react";
import { allDocPages } from "./content";
import DocsLayout from "./layout";

const markdown = `
The \`/chat\` endpoint is a **conversational, function-calling interface** to every CloudQuote API. It mirrors OpenAI's Chat Completions schema, so any client library that targets OpenAI works against CloudQuote with a single base-URL change.

You send messages, the LLM decides which CloudQuote endpoints to call, and you get back either a streaming answer or a fully synthesized response.

## Why use it

- **No endpoint memorization.** Ask in plain English; the LLM picks the right tools.
- **Multi-step reasoning for free.** A single user prompt can trigger \`getQuote\` → \`getNewsByTicker\` → \`getTechnicalIndicators\` and produce one coherent answer.
- **Streaming output.** Partial results land as the LLM produces them.
- **Same auth as the rest of the platform.** Your existing API key works.

## Endpoint: \`POST /chat\`

### Request

\`\`\`json
{
  "model": "cloudquote-chat-1",
  "messages": [
    { "role": "system", "content": "You are a financial data assistant." },
    { "role": "user", "content": "Show me Apple's current price and any news headlines from the last hour." }
  ],
  "tools": [
    {
      "name": "getQuote",
      "description": "Fetch current stock quote by symbol",
      "parameters": {
        "type": "object",
        "properties": {
          "symbol": { "type": "string", "description": "Ticker symbol, e.g. AAPL" }
        },
        "required": ["symbol"]
      }
    },
    {
      "name": "getNewsByTicker",
      "description": "Retrieve news headlines for a ticker within a time range",
      "parameters": {
        "type": "object",
        "properties": {
          "symbol": { "type": "string" },
          "startDate": { "type": "string", "format": "date-time" },
          "endDate": { "type": "string", "format": "date-time" }
        },
        "required": ["symbol", "startDate", "endDate"]
      }
    }
  ],
  "temperature": 0.2
}
\`\`\`

If you omit \`tools\`, the platform uses a curated default set covering the most common endpoints. Pass an explicit list to whitelist or extend that set.

### Response (function call step)

\`\`\`json
{
  "choices": [
    {
      "message": {
        "role": "assistant",
        "content": null,
        "function_call": {
          "name": "getQuote",
          "arguments": "{\\"symbol\\": \\"AAPL\\"}"
        }
      }
    }
  ]
}
\`\`\`

CloudQuote runs the call internally, feeds the result back into the model, and continues until the model produces a final \`content\` message:

> "As of 8:00 PM UTC, Apple Inc. (AAPL) is trading at \$174.21 (-0.47%). In the last hour, the top headlines are…"

## Streaming

Set \`stream: true\` to receive Server-Sent Events. You'll see tool-call steps, partial assistant content, and a final \`[DONE]\` marker — same shape as OpenAI's streaming protocol.

## Authentication and entitlements

Every \`/chat\` request flows through your existing key (private, public + user key, or whitelisted domain). For each tool the LLM tries to call, the platform checks your subscription:

- **Tool covered by your plan** → call runs, result is returned to the model.
- **Tool not covered** → you receive an error like \`{ "error": "Unauthorized to call getPriceHistory." }\` and the model is told it can't access that data, so it adapts (often suggesting an upgrade).

## Rate limits

Tool-level rate limits apply inside \`/chat\` too. If a limit is hit:

\`\`\`json
{ "error": "Rate limit exceeded for getQuote. Limit: 50/min." }
\`\`\`

The model is informed and can suggest waiting or upgrading.

## Patterns

### Conversational research

\`\`\`json
{
  "model": "cloudquote-chat-1",
  "messages": [
    { "role": "user", "content": "Recommend the top 5 S&P 500 stocks with P/E below 15, rising dividends, and a positive 50-day SMA cross." }
  ]
}
\`\`\`

The model orchestrates a multi-step plan: pull the universe, filter on fundamentals, check technical indicators, return a ranked list with rationales.

### Persistent context

The endpoint maintains conversation history. After the call above, the user can ask "Show me the 1-year chart for IBM with the 50-day SMA overlaid" — the model remembers IBM is one of its earlier picks and calls \`getPriceChart\` directly.

### Synthesized insights

The model can chain quotes + technicals + news to produce qualitative output: *"Combining Apple's positive guidance with the bullish MACD signal suggests potential upside over the next week."*

## When to use \`/chat\` vs MCP

- Use **\`/chat\`** when your client speaks the OpenAI Chat Completions protocol (the OpenAI SDK, the Anthropic SDK in OpenAI-compatibility mode, LangChain's OpenAI client, etc.).
- Use **MCP** when you're running an on-prem or third-party LLM that supports the [Model Context Protocol](/docs/mcp). The semantics are equivalent; the wire format differs.

## Where to go next

- [Building AI Agents](/docs/agents) — patterns for autonomous workflows on top of \`/chat\`.
- [MCP](/docs/mcp) — the model-agnostic alternative.
`;

export default function ChatApiPage(): ReactNode {
  const page = allDocPages.find(p => p.slug === "chat-api")!;
  return <DocsLayout page={page} markdown={markdown}/>;
}
